import {
  Box,
  createStyles,
  Grid,
  makeStyles,
  Paper,
  Theme,
  Typography,
  Card,
  CardContent,
} from '@material-ui/core';
import Divider from '@material-ui/core/Divider';
import { navigate } from '@reach/router';
import React from 'react';
import { parse } from 'date-fns';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';

import { useTranslation } from '../../../hooks/useTranslation';
import ROUTES from '../../../routes';
import {
  OneTimePayConfigData,
  OneTimePayConfirmationResponse,
} from '../oneTimePayment.types';
import { isWithInThreeDays } from '../oneTimePay.utils';
import Alerts from '../../alerts';
import PGEButton from '../../buttons';
import useAccountCustomer from '../../../hooks/useAccountCustomer';
import {
  toCurrencyString,
  toDateStringFullMonthName,
  truncateWithEllipses,
} from '../../../util/format';
import { isMobile } from '../../../util/style-utils';
import useStyles from '../payment-confirmation-form/PaymentConfirmation.styles';
import PaymentProfileIcon from '../../paymentus/payment-profile-icon/PaymentProfileIcon';
import useGTMUtilNmttp from '../../need-more-time-to-pay/gtm/useGTMUtilNmttp';
import { getPaymentMethodLabel } from '../../../hooks/usePaymentus';
import {
  PaymentCategory,
  PaymentConfirmation,
  PaymentProfile,
} from '../../../__generated__/pge-types';
import { DownloadAndPrint } from '../../download-print/DownloadAndPrint';
import colors from '../../../themes/main-colors';

import ErrorIcon from '@material-ui/icons/Error';

type Props = {
  path?: string;
  oneTimePayConfirmationData: OneTimePayConfirmationResponse;
  oneTimePayConfig: OneTimePayConfigData;
  paymentMethodCapLimit: number;
  redirectionURL?: string;
  isForReconnect?: boolean;
  printContainerId: string;
  doneUrl?: string;
  invoiceId?: string;
};

const useCardStyles = makeStyles((theme: Theme) =>
  createStyles({
    border: {
      justifyContent: 'center',
      padding: '2rem',
      backgroundColor: colors.riverBlue,
    },
    color: {
      color: theme.palette.success.main,
    },
    greenCheck: {
      color: colors.shoreGreen,
      fontSize: '3rem',
      [theme.breakpoints.down('sm')]: {
        fontSize: '2rem',
      },
    },
    failureIcon: {
      color: colors.darkRed,
      fontSize: '3rem',
      [theme.breakpoints.down('sm')]: {
        fontSize: '2rem',
      },
    },
    paymentOption: {
      color: colors.noirBlur,
      fontWeight: 'bold',
      fontSize: '28px',
      lineHeight: '20px',
      [theme.breakpoints.down('sm')]: {
        fontSize: '18px',
      },
    },
    paymentPaid: {
      color: colors.noirBlur,
      fontSize: '20px',
      [theme.breakpoints.down('md')]: {
        fontSize: '14px',
        lineHeight: '20px',
      },
    },
  }),
);

export default function PaymentConfirmationForm({
  oneTimePayConfirmationData,
  oneTimePayConfig,
  paymentMethodCapLimit,
  redirectionURL,
  isForReconnect,
  doneUrl,
  printContainerId,
  invoiceId,
}: Props) {
  const { richT, t } = useTranslation();
  const classes = useStyles();
  const styles = useCardStyles();
  const { customer } = useAccountCustomer();
  const {
    gtm_PaymentConfirmationForm_HandleOnTryAgainClick,
    gtm_PaymentConfirmationForm_HandleDoneMouseDown,
  } = useGTMUtilNmttp();
  const paymentDateIsWithInThreeDays = isWithInThreeDays(
    oneTimePayConfig.paymentDate,
  );
  const selectedProfileList =
    oneTimePayConfig.paymentSelector.selectedPaymentProfileList;

  const getFormattedPaymentAmount = (paymentAmount: string) => {
    return paymentAmount
      ? `$${toCurrencyString(parseFloat(paymentAmount), false)}`
      : '';
  };

  const successProfiles = oneTimePayConfirmationData.paymentConfirmation.filter(
    p => p.isSuccess === true,
  );
  const navigateToMakePayment = () => {
    // gtm: only register NMTTP/TPA data if it's coming from TPA flow...
    redirectionURL === ROUTES.TPA_MAKE_PAYMENT
      ? gtm_PaymentConfirmationForm_HandleOnTryAgainClick()
      : null;
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    isForReconnect
      ? navigate(ROUTES.RECONNECT_SERVICE_START)
      : navigate(redirectionURL || ROUTES.PAYMENT);
  };

  const handleDoneMouseDown = () => {
    gtm_PaymentConfirmationForm_HandleDoneMouseDown();

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    navigate(doneUrl ? doneUrl : ROUTES.ACCOUNT);
  };

  const getMethodDisplayLabel = (paymentConfirmation: PaymentConfirmation) =>
    getPaymentMethodLabel(
      paymentConfirmation.cardType
        ? paymentConfirmation.paymentMethod === 'DC'
          ? paymentConfirmation.cardType + '_DEBIT'
          : paymentConfirmation.cardType
        : paymentConfirmation.paymentMethod || '',
      true,
    );

  const getMaskedAccountDisplayLabel = (
    paymentConfirmation: PaymentConfirmation,
    paymentProfile: PaymentProfile,
  ) => {
    return [PaymentCategory.PaypalAccount, PaymentCategory.PaypalCredit]
      .map(p => p.toString())
      .includes(paymentConfirmation.cardType)
      ? paymentProfile.cardHolderName
        ? truncateWithEllipses(paymentConfirmation.digitalWalletEmail, 7)
        : t('GUEST_PAY_AUTHORIZED')
      : '******' + paymentConfirmation.lastFourDigitsCardNumber;
  };

  if (oneTimePayConfirmationData.isPartialSucceed) {
    return (
      <>
        <Grid container className={classes.marginTop1}>
          <Grid item xs={12} className={classes.marginBot1}>
            <Typography
              className={classes.colorTextPrimary}
              component={'span'}
              variant={'h2'}
            >
              {t('PAYMENT_CONFIRMATION')}
            </Typography>
          </Grid>
          <Paper
            component={'main'}
            className={styles.border}
            style={{ marginBottom: '24px' }}
          >
            <Box style={{ backgroundColor: 'white' }}>
              <Grid
                container
                style={{ textAlign: 'center', padding: '0 1rem' }}
              >
                <Grid item xs={12}>
                  <Grid item style={{ marginTop: '8px' }}>
                    <ErrorIcon className={styles.greenCheck} />
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Grid item>
                    <Typography
                      css={{
                        '& p': {
                          marginBottom: 0,
                        },
                      }}
                      className={styles.paymentOption}
                    >
                      {t('PARTALLY_SUCCEED')}
                    </Typography>
                  </Grid>
                </Grid>

                {oneTimePayConfirmationData.paymentConfirmation.map(
                  (paymentProfile, index) => {
                    return (
                      <>
                        <Grid container style={{ justifyContent: 'center' }}>
                          <Grid
                            item
                            css={{
                              '& p': {
                                margin: '0',
                              },
                            }}
                          >
                            {paymentProfile.isSuccess ? (
                              <Typography
                                className={`${classes.paymentmethodLabel} ${classes.noWrap}`}
                                component="span"
                              >
                                {richT('ACCOUNT_PAYMENT_MESSAGE', {
                                  ACCOUNT_PAYMENT: getFormattedPaymentAmount(
                                    paymentProfile?.paymentAmount.toFixed(2),
                                  ),
                                  PAYMENT_DATE: oneTimePayConfig.paymentDate
                                    ? toDateStringFullMonthName(
                                        parse(
                                          oneTimePayConfig.paymentDate,
                                          'MM/dd/yyyy',
                                          new Date(),
                                        ),
                                      )
                                    : '',
                                })}
                              </Typography>
                            ) : (
                              <Typography
                                variant={'body1'}
                                className={`${classes.paymentmethodLabel} ${classes.noWrap}`}
                                component="span"
                              >
                                {richT('ACCOUNT_PAYMENT_FAILED_MESSAGE', {
                                  ACCOUNT_PAYMENT: getFormattedPaymentAmount(
                                    paymentProfile?.paymentAmount.toFixed(2),
                                  ),
                                  PAYMENT_DATE: oneTimePayConfig.paymentDate
                                    ? toDateStringFullMonthName(
                                        parse(
                                          oneTimePayConfig.paymentDate,
                                          'MM/dd/yyyy',
                                          new Date(),
                                        ),
                                      )
                                    : '',
                                })}
                              </Typography>
                            )}
                          </Grid>

                          <Grid item>
                            <Typography
                              variant={'body1'}
                              className={`${classes.paymentmethodLabel} ${classes.noWrap}`}
                              component="span"
                            >
                              {getMethodDisplayLabel(paymentProfile)}
                            </Typography>
                            <Typography
                              variant={'body1'}
                              className={`${classes.paymentmethodLabel} ${classes.noWrap}`}
                              component="span"
                            >
                              <PaymentProfileIcon
                                type={
                                  paymentProfile.cardType
                                    ? paymentProfile.cardType === 'GOOGLE_PAY'
                                      ? PaymentCategory.Gp
                                      : paymentProfile.cardType === 'APPLE_PAY'
                                      ? PaymentCategory.Ap
                                      : paymentProfile.cardType
                                    : paymentProfile.paymentMethod
                                }
                              ></PaymentProfileIcon>
                              <Typography
                                variant={'h2'}
                                component="span"
                                style={{
                                  verticalAlign: 'middle',
                                }}
                              >
                                {getMaskedAccountDisplayLabel(
                                  paymentProfile,
                                  oneTimePayConfig.paymentSelector
                                    .selectedPaymentProfileList[index].profile,
                                )}
                              </Typography>
                            </Typography>
                          </Grid>
                        </Grid>

                        {paymentProfile.isSuccess ? (
                          <>
                            <Grid
                              item
                              xs={12}
                              className={classes.paymentMethodContainer}
                            >
                              <Grid
                                item
                                xs={12}
                                style={{
                                  display: 'inline-flex',
                                  textAlign: 'center',
                                }}
                              >
                                <Typography
                                  className={classes.confirmationNumber}
                                  variant={'body1'}
                                >
                                  {t('ONETIMEPAY_YOUR_CONFIRMATION_NEW')}
                                </Typography>
                                <Typography
                                  className={classes.confirmationValue}
                                >
                                  <strong>
                                    {paymentProfile.confirmationId}
                                  </strong>
                                </Typography>
                              </Grid>
                            </Grid>

                            <Grid
                              item
                              xs={12}
                              style={{ display: 'inline-block' }}
                            >
                              <Grid
                                item
                                xs={12}
                                style={{
                                  display: 'flex',
                                  justifyContent: 'center',
                                }}
                              >
                                <Typography
                                  className={classes.colorTextPrimary}
                                >
                                  {richT('PAYMENT_RECEIPT_MESSAGE', {
                                    EMAIL: customer?.email,
                                  })}
                                </Typography>
                              </Grid>
                            </Grid>
                            {paymentDateIsWithInThreeDays && (
                              <Grid item xs={12}>
                                <Typography
                                  className={classes.colorTextPrimary}
                                >
                                  {t('PAYMENT_DEDUCTION_SLUG')}.
                                </Typography>
                              </Grid>
                            )}
                            {!paymentDateIsWithInThreeDays && (
                              <Grid item xs={12} className={classes.marginTop1}>
                                <Typography
                                  className={classes.colorTextPrimary}
                                  component={'span'}
                                >
                                  {richT(
                                    'ONETIMEPAY_CAN_CANCEL_PAYMENT_MESSAGE',
                                    {
                                      CALL_FOR_ASSISTANCE_NUMBER: t(
                                        'CALL_FOR_ASSISTANCE_NUMBER',
                                      ),
                                    },
                                  )}
                                </Typography>
                              </Grid>
                            )}
                            {oneTimePayConfirmationData.paymentConfirmation.some(
                              p =>
                                p?.pendingDisconnect?.isPendingDisconnect ===
                                true,
                            ) ? (
                              <Grid item xs={12} className={classes.marginTop1}>
                                <Typography
                                  className={classes.colorTextDanger}
                                  component={'span'}
                                >
                                  {richT(
                                    'MULTIPAY_PENDING_DISCONNECT_WARNING',
                                    {
                                      ASSISTANCE_NUMBER: t(
                                        'CALL_FOR_ASSISTANCE_NUMBER',
                                      ),
                                    },
                                  )}
                                </Typography>
                              </Grid>
                            ) : null}
                          </>
                        ) : (
                          paymentProfile?.errorMessage && (
                            <Grid
                              item
                              xs={12}
                              style={{
                                marginTop: '16px',
                                marginBottom: '16px',
                              }}
                            >
                              <Typography
                                className={classes.colorTextPrimary}
                                component={'span'}
                              >
                                {t(paymentProfile?.errorMessage)}
                              </Typography>
                            </Grid>
                          )
                        )}
                        {oneTimePayConfirmationData.paymentConfirmation.length -
                          1 <
                          index && <Divider />}
                      </>
                    );
                  },
                )}
              </Grid>
            </Box>
          </Paper>
          <Grid
            item
            xs={12}
            className={classes.buttonContainer}
            style={{ paddingRight: 0 }}
            data-noprint
          >
            <PGEButton
              size={'large'}
              onMouseDown={navigateToMakePayment}
              variant={'outlined'}
              color={'secondary'}
              className={classes.tryAgain}
            >
              {t('TRY_AGAIN')}
            </PGEButton>
            <PGEButton
              size={'large'}
              onMouseDown={() => navigate(ROUTES.ACCOUNT)}
              type={'submit'}
              variant={'contained'}
              color={'primary'}
              className={classes.submit}
            >
              {t('DONE')}
            </PGEButton>
          </Grid>
        </Grid>
      </>
    );
  } else {
    const isAllSuccess = oneTimePayConfirmationData.paymentConfirmation.every(
      payment => payment.isSuccess === true,
    );
    return (
      <>
        <Grid item container direction="row" justify="flex-end">
          <Grid item>
            <DownloadAndPrint
              containerId={printContainerId}
              excludeSelectors={['[data-noprint]']}
              minHeight={
                isForReconnect || (redirectionURL && !invoiceId)
                  ? 100
                  : undefined
              }
            ></DownloadAndPrint>
          </Grid>
        </Grid>
        <Paper component={'main'} className={styles.border}>
          <Box style={{ backgroundColor: 'white' }}>
            <Grid container spacing={4}>
              <Grid item xs={12}>
                <Grid container item xs={12}>
                  <Grid container item>
                    <Grid
                      container
                      style={{ textAlign: 'center', padding: '0 1rem' }}
                    >
                      <Grid item xs={12}>
                        <Grid item>
                          <Typography>
                            {isAllSuccess ? (
                              <CheckCircleIcon className={styles.greenCheck} />
                            ) : (
                              <ErrorIcon className={styles.failureIcon} />
                            )}
                          </Typography>
                        </Grid>
                        {isAllSuccess && (
                          <>
                            <Grid item>
                              <Typography
                                variant="body1"
                                css={{
                                  fontSize: 20,
                                  '& p': {
                                    marginTop: 0,
                                  },
                                }}
                              >
                                {richT('TOTAL_PAYMENT_MESSAGE', {
                                  TOTAL_PAYMENT:
                                    oneTimePayConfig?.charitySelection &&
                                    oneTimePayConfig?.charitySelection !==
                                      'none'
                                      ? getFormattedPaymentAmount(
                                          (
                                            Number(
                                              oneTimePayConfig?.paymentAmount,
                                            ) +
                                            Number(
                                              oneTimePayConfig?.charityAmount,
                                            )
                                          ).toFixed(2),
                                        )
                                      : getFormattedPaymentAmount(
                                          oneTimePayConfig?.paymentAmount,
                                        ),
                                })}
                              </Typography>
                            </Grid>

                            {oneTimePayConfirmationData?.paymentConfirmation &&
                            oneTimePayConfirmationData?.paymentConfirmation
                              .length > 0 &&
                            oneTimePayConfirmationData?.paymentConfirmation[0]
                              ?.paymentFeeAmount &&
                            Number(
                              oneTimePayConfirmationData?.paymentConfirmation[0]
                                ?.paymentFeeAmount,
                            ) > 0 ? (
                              <Grid item>
                                <Typography
                                  variant="body1"
                                  css={{
                                    fontSize: 20,
                                    fontWeight: 'bold',
                                    marginBottom: '0.5rem',
                                  }}
                                >
                                  {t('WITH_CONVENINENCE_FEE') +
                                    ' ' +
                                    getFormattedPaymentAmount(
                                      Number(
                                        oneTimePayConfirmationData
                                          ?.paymentConfirmation[0]
                                          ?.paymentFeeAmount,
                                      ).toFixed(2),
                                    )}
                                </Typography>
                              </Grid>
                            ) : null}
                          </>
                        )}
                        <Grid item>
                          <Typography
                            variant="body1"
                            className={styles.paymentOption}
                            css={{
                              '& p': {
                                margin: 0,
                              },
                            }}
                          >
                            {isAllSuccess
                              ? richT('PAYMENT_SUCCESS_MESSAGE')
                              : richT('PAYMENT_FAILURE_MESSAGE')}
                          </Typography>
                        </Grid>
                        {isAllSuccess ? (
                          <>
                            <Grid item>
                              {oneTimePayConfig?.charitySelection &&
                              oneTimePayConfig?.charitySelection !== 'none' ? (
                                <Typography
                                  variant="body1"
                                  className={styles.paymentPaid}
                                >
                                  {richT(
                                    'ACCOUNT_AND_CHARITY_PAYMENT_MESSAGE',
                                    {
                                      ACCOUNT_PAYMENT: getFormattedPaymentAmount(
                                        oneTimePayConfig?.paymentAmount,
                                      ),
                                      CHARITY_AMOUNT: getFormattedPaymentAmount(
                                        oneTimePayConfig?.charityAmount!,
                                      ),
                                      PAYMENT_DATE: oneTimePayConfig.paymentDate
                                        ? toDateStringFullMonthName(
                                            parse(
                                              oneTimePayConfig.paymentDate,
                                              'MM/dd/yyyy',
                                              new Date(),
                                            ),
                                          )
                                        : '',
                                    },
                                  )}
                                </Typography>
                              ) : (
                                <Typography
                                  variant="body1"
                                  className={styles.paymentPaid}
                                >
                                  {invoiceId
                                    ? richT('INVOICE_PAYMENT_MESSAGE', {
                                        INVOICE_PAYMENT: getFormattedPaymentAmount(
                                          oneTimePayConfig?.paymentAmount,
                                        ),
                                        INVOICE_ID: invoiceId,
                                        PAYMENT_DATE: oneTimePayConfig.paymentDate
                                          ? toDateStringFullMonthName(
                                              parse(
                                                oneTimePayConfig.paymentDate,
                                                'MM/dd/yyyy',
                                                new Date(),
                                              ),
                                            )
                                          : '',
                                      })
                                    : richT('ACCOUNT_PAYMENT_HEADING_MESSAGE', {
                                        ACCOUNT_PAYMENT: getFormattedPaymentAmount(
                                          oneTimePayConfig?.paymentAmount,
                                        ),
                                        PAYMENT_DATE: oneTimePayConfig.paymentDate
                                          ? toDateStringFullMonthName(
                                              parse(
                                                oneTimePayConfig.paymentDate,
                                                'MM/dd/yyyy',
                                                new Date(),
                                              ),
                                            )
                                          : '',
                                      })}
                                </Typography>
                              )}
                            </Grid>
                          </>
                        ) : (
                          <>
                            {' '}
                            <Typography
                              variant={'body1'}
                              className={`${classes.paymentmethodLabel}`}
                              component="span"
                              css={{
                                '& p': {
                                  marginTop: 0,
                                },
                              }}
                            >
                              {oneTimePayConfig?.charitySelection &&
                              oneTimePayConfig?.charitySelection !== 'none'
                                ? richT('ACCOUNT_AND_CHARITY_PAYMNET_FAILED', {
                                    ACCOUNT_PAYMENT: getFormattedPaymentAmount(
                                      oneTimePayConfig?.paymentAmount,
                                    ),
                                    CHARITY_AMOUNT: getFormattedPaymentAmount(
                                      oneTimePayConfig?.charityAmount!,
                                    ),
                                    PAYMENT_DATE: oneTimePayConfig.paymentDate
                                      ? toDateStringFullMonthName(
                                          parse(
                                            oneTimePayConfig.paymentDate,
                                            'MM/dd/yyyy',
                                            new Date(),
                                          ),
                                        )
                                      : '',
                                  })
                                : richT('ACCOUNT_FAILED_FOR_MULTIPLE_PAYMENT', {
                                    ACCOUNT_PAYMENT: getFormattedPaymentAmount(
                                      oneTimePayConfig?.paymentAmount,
                                    ),
                                    PAYMENT_DATE: oneTimePayConfig.paymentDate
                                      ? toDateStringFullMonthName(
                                          parse(
                                            oneTimePayConfig.paymentDate,
                                            'MM/dd/yyyy',
                                            new Date(),
                                          ),
                                        )
                                      : '',
                                  })}
                            </Typography>
                          </>
                        )}

                        {selectedProfileList.length ===
                        paymentMethodCapLimit ? (
                          <Divider />
                        ) : (
                          <Box style={{ marginBottom: '10px' }}>
                            <Divider />
                          </Box>
                        )}

                        {oneTimePayConfirmationData.paymentConfirmation.map(
                          (paymentProfile, index) => {
                            return (
                              <div key={index}>
                                <Grid
                                  item
                                  xs={12}
                                  className={classes.paymentMethodContainer}
                                >
                                  <Grid
                                    item
                                    style={
                                      !isMobile()
                                        ? {
                                            display: 'flex',
                                            textAlign: 'center',
                                            alignItems: 'center',
                                          }
                                        : {}
                                    }
                                  >
                                    {selectedProfileList.length ===
                                    paymentMethodCapLimit ? (
                                      <Typography
                                        className={`${classes.colorTextPrimary} ${classes.noWrap}`}
                                      >
                                        {richT(
                                          'ONETIMEPAY_PAYMENT_AMOUNT_USING',
                                          {
                                            PAYMENT_AMOUNT: paymentProfile.paymentAmount.toFixed(
                                              2,
                                            ),
                                          },
                                        )}
                                      </Typography>
                                    ) : (
                                      <Typography
                                        className={`${classes.colorTextPrimary} ${classes.noWrap}`}
                                      >
                                        {t('ONETIMEPAY_PAID_WITH') +
                                          ' ' +
                                          getMethodDisplayLabel(paymentProfile)}
                                      </Typography>
                                    )}

                                    {selectedProfileList.length ===
                                    paymentMethodCapLimit ? (
                                      <div
                                        style={
                                          !isMobile()
                                            ? {
                                                width: '100%',
                                                marginTop: '0.2em',
                                              }
                                            : { width: '100%' }
                                        }
                                      >
                                        <Typography
                                          variant={'body1'}
                                          className={`${classes.paymentmethodLabel} ${classes.noWrap}`}
                                          component="span"
                                        >
                                          {getMethodDisplayLabel(
                                            paymentProfile,
                                          )}

                                          <Typography
                                            className={
                                              classes.paymentMethodIcon
                                            }
                                            style={{
                                              width: isMobile()
                                                ? '100%'
                                                : '90%',
                                              marginLeft: isMobile()
                                                ? 0
                                                : selectedProfileList.length ===
                                                  paymentMethodCapLimit
                                                ? '0.5em'
                                                : 0,
                                            }}
                                            component="span"
                                            variant={'body2'}
                                          >
                                            <PaymentProfileIcon
                                              type={
                                                paymentProfile.cardType
                                                  ? paymentProfile.cardType ===
                                                    'GOOGLE_PAY'
                                                    ? PaymentCategory.Gp
                                                    : paymentProfile.cardType ===
                                                      'APPLE_PAY'
                                                    ? PaymentCategory.Ap
                                                    : paymentProfile.cardType
                                                  : paymentProfile.paymentMethod
                                              }
                                            ></PaymentProfileIcon>
                                            <Typography
                                              variant={'h2'}
                                              component="span"
                                              style={{
                                                verticalAlign: 'middle',
                                              }}
                                            >
                                              {getMaskedAccountDisplayLabel(
                                                paymentProfile,
                                                oneTimePayConfig.paymentSelector
                                                  .selectedPaymentProfileList[
                                                  index
                                                ].profile,
                                              )}
                                            </Typography>
                                          </Typography>
                                        </Typography>
                                      </div>
                                    ) : (
                                      <div style={{ width: '100%' }}>
                                        <Typography
                                          className={classes.paymentMethodIcon}
                                          style={{
                                            width: isMobile() ? '100%' : '90%',
                                            marginLeft: isMobile()
                                              ? 0
                                              : selectedProfileList.length ===
                                                paymentMethodCapLimit
                                              ? '0.5em'
                                              : 0,
                                          }}
                                          component="span"
                                          variant={'body2'}
                                        >
                                          <PaymentProfileIcon
                                            type={
                                              paymentProfile.cardType
                                                ? paymentProfile.cardType ===
                                                  'GOOGLE_PAY'
                                                  ? PaymentCategory.Gp
                                                  : paymentProfile.cardType ===
                                                    'APPLE_PAY'
                                                  ? PaymentCategory.Ap
                                                  : paymentProfile.cardType
                                                : paymentProfile.paymentMethod
                                            }
                                          ></PaymentProfileIcon>
                                          <Typography
                                            variant={'h2'}
                                            component="span"
                                            style={{
                                              verticalAlign: 'middle',
                                            }}
                                          >
                                            {getMaskedAccountDisplayLabel(
                                              paymentProfile,
                                              oneTimePayConfig.paymentSelector
                                                .selectedPaymentProfileList[
                                                index
                                              ].profile,
                                            )}
                                          </Typography>
                                        </Typography>
                                      </div>
                                    )}
                                  </Grid>
                                </Grid>
                                {isAllSuccess ? (
                                  <Grid
                                    item
                                    xs={12}
                                    className={classes.paymentMethodContainer}
                                  >
                                    <Grid
                                      item
                                      xs={12}
                                      style={{
                                        display: 'flex',
                                        textAlign: 'center',
                                      }}
                                    >
                                      <Typography
                                        className={classes.confirmationNumber}
                                        variant={'body1'}
                                      >
                                        {t('ONETIMEPAY_YOUR_CONFIRMATION_NEW')}
                                      </Typography>
                                      <Typography
                                        className={classes.confirmationValue}
                                      >
                                        <strong>
                                          {paymentProfile.confirmationId}
                                        </strong>
                                      </Typography>
                                    </Grid>
                                  </Grid>
                                ) : (
                                  paymentProfile?.errorMessage && (
                                    <>
                                      <Grid
                                        item
                                        xs={12}
                                        style={{
                                          marginTop: '16px',
                                          marginBottom: '16px',
                                        }}
                                      >
                                        <Typography
                                          className={classes.colorTextPrimary}
                                          component={'span'}
                                        >
                                          {t(paymentProfile?.errorMessage)}
                                        </Typography>
                                      </Grid>
                                    </>
                                  )
                                )}

                                {oneTimePayConfirmationData.paymentConfirmation
                                  .length -
                                  1 <
                                index ? (
                                  <>
                                    <div className={classes.padding1} />
                                    <Divider />
                                  </>
                                ) : null}
                              </div>
                            );
                          },
                        )}

                        {isAllSuccess ? (
                          <>
                            {selectedProfileList.length ===
                            paymentMethodCapLimit ? (
                              <>
                                <div className={classes.padding1} />
                                <Divider />
                              </>
                            ) : null}
                            <Grid
                              item
                              xs={12}
                              style={{ display: 'inline-block' }}
                            >
                              <Grid
                                item
                                xs={12}
                                style={{ display: 'flex', textAlign: 'center' }}
                              >
                                <Typography
                                  className={classes.colorTextPrimary}
                                >
                                  {richT('PAYMENT_RECEIPT_MESSAGE', {
                                    EMAIL: customer?.email,
                                  })}
                                </Typography>
                              </Grid>
                            </Grid>
                            {paymentDateIsWithInThreeDays && (
                              <Grid item xs={12}>
                                <Typography
                                  className={classes.colorTextPrimary}
                                >
                                  {t('PAYMENT_DEDUCTION_SLUG')}.
                                </Typography>
                              </Grid>
                            )}
                            {!paymentDateIsWithInThreeDays && (
                              <Grid item xs={12} className={classes.marginTop1}>
                                <Typography
                                  className={classes.colorTextPrimary}
                                  component={'span'}
                                >
                                  {richT(
                                    'ONETIMEPAY_CAN_CANCEL_PAYMENT_MESSAGE',
                                    {
                                      CALL_FOR_ASSISTANCE_NUMBER: t(
                                        'CALL_FOR_ASSISTANCE_NUMBER',
                                      ),
                                    },
                                  )}
                                </Typography>
                              </Grid>
                            )}
                            {oneTimePayConfirmationData.paymentConfirmation.some(
                              p =>
                                p?.pendingDisconnect?.isPendingDisconnect ===
                                true,
                            ) ? (
                              <Grid item xs={12} className={classes.marginTop1}>
                                <Typography
                                  className={classes.colorTextDanger}
                                  component={'span'}
                                >
                                  {richT(
                                    'MULTIPAY_PENDING_DISCONNECT_WARNING',
                                    {
                                      ASSISTANCE_NUMBER: t(
                                        'CALL_FOR_ASSISTANCE_NUMBER',
                                      ),
                                    },
                                  )}
                                </Typography>
                              </Grid>
                            ) : null}
                          </>
                        ) : null}
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </Paper>

        <Grid
          item
          xs={12}
          className={`${classes.buttonContainer} ${classes.marginTop1}`}
          style={{ paddingRight: 0 }}
          data-noprint
        >
          {!isAllSuccess && (
            <PGEButton
              size={'large'}
              onMouseDown={navigateToMakePayment}
              variant={'outlined'}
              color={'secondary'}
              className={classes.tryAgain}
            >
              {t('TRY_AGAIN')}
            </PGEButton>
          )}

          <PGEButton
            data-testid={'pay-bill-button'}
            size={'large'}
            onMouseDown={handleDoneMouseDown}
            type={'submit'}
            variant={'contained'}
            color={'primary'}
            className={classes.submit}
          >
            {t('DONE')}
          </PGEButton>
        </Grid>
      </>
    );
  }
}
